import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Activity, ItineraryDay, TripPhoto } from '../../types';
import type { StopSuggestion } from '../../api/photos.api';
import type { SuggestionReview } from '../../hooks/usePhotoStopSuggestions';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import { Icon, MapPin, Sparkles } from '../ui/Icon';

interface Props {
  review: SuggestionReview;
  days: ItineraryDay[];
  busy: boolean;
  onApply: (accepted: { photo: TripPhoto; stop: Activity }[]) => void;
  onClose: () => void;
}

interface Row {
  photo: TripPhoto;
  suggestion: StopSuggestion | undefined;
  stop: Activity | undefined;
  /** Already on that stop: nothing to apply. */
  alreadyThere: boolean;
}

/**
 * ADR-0058: nothing the AI says is applied until the user looks at it. A
 * low-confidence guess starts unticked.
 */
export default function StopSuggestionReview({ review, days, busy, onApply, onClose }: Props) {
  const { t } = useTranslation();
  const stops = useMemo(() => new Map(days.flatMap(d => d.activities.map(a => [a.id, a] as const))), [days]);
  const dayNumber = useMemo(() => new Map(days.map(d => [d.id, d.dayNumber] as const)), [days]);

  const rows: Row[] = useMemo(() => review.photos.map(photo => {
    const suggestion = review.suggestions?.find(s => s.photoId === photo.id);
    const stop = suggestion?.activityId ? stops.get(suggestion.activityId) : undefined;
    return { photo, suggestion, stop, alreadyThere: !!stop && photo.activityId === stop.id };
  }), [review, stops]);

  const [ticked, setTicked] = useState<Set<string> | null>(null);
  const defaultTicked = useMemo(
    () => new Set(rows.filter(r => r.stop && !r.alreadyThere && r.suggestion?.confidence === 'high').map(r => r.photo.id)),
    [rows],
  );
  const selected = ticked ?? defaultTicked;
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setTicked(next);
  };

  const loading = review.suggestions === null;
  const found = rows.filter(r => r.stop).length;

  return (
    <Modal open onClose={onClose} title={t('gallery.suggest.title')} size="lg">
      {loading ? (
        <div className="flex flex-col items-center gap-3 py-10 text-sm text-slate-500">
          <Spinner />
          {t('gallery.suggest.looking', { count: review.photos.length })}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('gallery.suggest.summary', { found, total: rows.length })}
          </p>

          <ul className="divide-y divide-slate-100 dark:divide-slate-800 -mx-2">
            {rows.map(({ photo, stop, suggestion, alreadyThere }) => {
              const pickable = !!stop && !alreadyThere;
              return (
                <li key={photo.id}>
                  <label className={`flex items-center gap-3 px-2 py-2 rounded-lg ${pickable ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(photo.id)}
                      disabled={!pickable}
                      onChange={() => toggle(photo.id)}
                      className="w-4 h-4 accent-teal-600 shrink-0 disabled:opacity-30"
                    />
                    <img
                      src={tripPhotoUrl(photo.thumbnailFilename ?? photo.filename)}
                      alt=""
                      className="w-14 h-14 rounded-lg object-cover shrink-0 bg-slate-100 dark:bg-slate-800"
                    />
                    <div className="min-w-0 flex-1">
                      {stop ? (
                        <>
                          <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800 dark:text-slate-100">
                            <Icon as={MapPin} size={13} className="text-teal-600 shrink-0" />
                            <span className="truncate">{stop.title}</span>
                          </p>
                          <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                            {t('gallery.dayOption', { number: dayNumber.get(stop.dayId) })}
                            {alreadyThere && <span>· {t('gallery.suggest.alreadyThere')}</span>}
                            {!alreadyThere && suggestion?.confidence === 'low' && (
                              <span className="px-1.5 py-px rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold">
                                {t('gallery.suggest.unsure')}
                              </span>
                            )}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-slate-400">{t('gallery.suggest.none')}</p>
                      )}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
            <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <Icon as={Sparkles} size={12} className="shrink-0" />
              {t('gallery.suggest.privacy')}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={onClose} className="btn-secondary">{t('common.cancel')}</button>
              <button
                onClick={() => onApply(rows.flatMap(r => r.stop && selected.has(r.photo.id) ? [{ photo: r.photo, stop: r.stop }] : []))}
                disabled={busy || selected.size === 0}
                className="btn-primary"
              >
                {busy ? <Spinner size="sm" /> : t('gallery.suggest.apply', { count: selected.size })}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
